import type { SurveyDay, SurveyFigures, SurveyPayload, SurveyPeriod, SurveyWeek } from './report';

/*
 * Two fallbacks for when the live report is not available:
 *
 *   previewPayload(asOf) — the design's figures (claude.ai design "Système U
 *     Quiz — two seasons dashboard") as they stand on a given day. DEV ONLY, so
 *     the page can be reviewed against the design before the pipeline emits
 *     seasons. SU_PREVIEW is the design's default snapshot, 9 Dec 2026.
 *   SU_EMPTY — the same structure with nothing measured. What a DEPLOYED build
 *     shows: a client is never served invented figures.
 *
 * Calendar (Season 1 cut short, 2026-11-03): Season 1 runs Tue 6 Oct → Sun 1 Nov
 * (four 7-day weeks, Tuesday → Monday, the last one six days), the game is
 * closed 2–16 Nov, and Season 2 runs Tue 17 Nov → Sun 13 Dec (four weeks, the
 * last one six days). Weeks are numbered 1–8 across the campaign.
 *
 * The design scales each week's full figures by how much of it has passed
 * (players grow slower than plays: f^0.6), and a season's distinct counts by
 * its share of plays (^0.7); 42 % of Season 2's members were also in Season 1.
 * Copied here so the preview moves the way the design's "as of" switch does.
 */

const GO_LIVE = '2026-10-06';
const DAY_MS = 86_400_000;
const iso = (offsetDays: number) => new Date(Date.parse(`${GO_LIVE}T00:00:00Z`) + offsetDays * DAY_MS).toISOString().slice(0, 10);
const dayIndex = (date: string) => Math.round((Date.parse(`${date}T00:00:00Z`) - Date.parse(`${GO_LIVE}T00:00:00Z`)) / DAY_MS);

type SeasonDef = {
  id: string;
  theme: string;
  /** Day indexes from go-live, inclusive. */
  from: number;
  to: number;
  /** The season's full distinct counts (design). */
  full: { visitors: number; optIns: number; playersStarted: number; players: number; emailOnly: number; both: number; smsOnly: number };
  /** Per week: players, quizzes started, plays, hours in app, % playing 1 / 2 / 3 times. */
  weeks: Array<[number, number, number, number, number, number, number]>;
};

const SEASONS: SeasonDef[] = [
  {
    id: 'p1',
    theme: "Les Fruits et Légumes d'Automne",
    from: 0,
    to: 26,
    full: { visitors: 231_480, optIns: 211_790, playersStarted: 208_450, players: 201_360, emailOnly: 15_630, both: 3, smsOnly: 31_450 },
    weeks: [
      [76_433, 122_313, 115_915, 5_135, 67, 14, 19],
      [52_077, 97_039, 91_820, 3_692, 54, 15, 31],
      [65_460, 118_729, 112_607, 4_329, 57, 15, 28],
      [58_920, 104_870, 99_410, 3_870, 58, 19, 23],
    ],
  },
  {
    id: 'p2',
    theme: 'Les Engagements et Filières U',
    from: 42,
    to: 68,
    full: { visitors: 168_400, optIns: 154_300, playersStarted: 151_900, players: 146_800, emailOnly: 11_100, both: 2, smsOnly: 22_600 },
    weeks: [
      [74_120, 118_900, 112_760, 4_890, 64, 15, 21],
      [58_310, 104_200, 98_900, 3_920, 56, 16, 28],
      [55_640, 99_800, 94_730, 3_690, 57, 16, 27],
      [47_820, 82_100, 77_950, 3_020, 60, 17, 23],
    ],
  },
];

/** Share of Season 2's members who were also in Season 1. */
const OVERLAP = 0.42;
/** Visitors per survey submitted, for the daily sample. */
const VISITORS_PER_PLAY = 1.4;
const WEEKDAY_WEIGHT = [1.25, 1, 0.92, 0.95, 1, 1.12, 1.2]; // Sun … Sat

const LAST_DAY = SEASONS[SEASONS.length - 1].to;
const TOTAL_WEEKS = SEASONS.reduce((a, s) => a + s.weeks.length, 0);

/** The weeks' day ranges, numbered across the campaign. */
const WEEK_SPANS = SEASONS.flatMap((s, si) =>
  s.weeks.map((_w, k) => {
    const from = s.from + 7 * k;
    return { season: si, k, from, to: k === s.weeks.length - 1 ? s.to : from + 6 };
  }),
).map((w, i) => ({ ...w, week: i + 1 }));

const consentsOf = (emailOnly: number, both: number, smsOnly: number) => ({
  total: emailOnly + both + smsOnly,
  emailOnly,
  emailAndSms: both,
  smsOnly,
  email: emailOnly + both,
  sms: smsOnly + both,
});

const blankWeek = (span: (typeof WEEK_SPANS)[number]): SurveyWeek => ({
  week: span.week,
  start: iso(span.from),
  end: iso(span.to),
  periodId: SEASONS[span.season].id,
  status: 'planned',
  uniquePlayers: null,
  plays: null,
  ticketsIssued: null,
  timeInAppSec: null,
  playsDistributionPct: [],
  quizzesStarted: null,
  playersStarted: null,
});

const blankPeriods = (): SurveyPeriod[] =>
  SEASONS.map((s, i) => ({
    id: s.id,
    label: `Season ${i + 1}`,
    theme: s.theme,
    weeks: WEEK_SPANS.filter((w) => w.season === i).map((w) => w.week),
    start: iso(s.from),
    end: iso(s.to),
    totals: null,
  }));

const ZERO: SurveyFigures = {
  uniqueVisitors: 0,
  optIns: 0,
  uniquePlayers: 0,
  plays: 0,
  ticketsIssued: 0,
  timeInAppSec: null,
  consents: consentsOf(0, 0, 0),
  quizzesStarted: null,
  playersStarted: null,
};

/** The design's dashboard as it stands at the end of `asOf` (a campaign day). */
export function previewPayload(asOf: string): SurveyPayload {
  const a = Math.min(LAST_DAY, Math.max(-1, dayIndex(asOf)));
  const ended = dayIndex(asOf) >= LAST_DAY;

  // Weeks: full figures scaled by the part of the week that has passed.
  const weeks = WEEK_SPANS.map((span) => {
    const [players, started, plays, hours, d1, d2, d3] = SEASONS[span.season].weeks[span.k];
    const len = span.to - span.from + 1;
    if (a < span.from) return { week: blankWeek(span), f: 0, players: 0, started: 0, plays: 0, hours: 0 };
    const done = ended || a > span.to;
    const f = done ? 1 : (a - span.from + 1) / len;
    const w = { players: Math.round(players * f ** 0.6), started: Math.round(started * f), plays: Math.round(plays * f), hours: hours * f };
    const ratio = SEASONS[span.season].full.playersStarted / SEASONS[span.season].full.players;
    const week: SurveyWeek = {
      ...blankWeek(span),
      status: done ? 'done' : 'live',
      uniquePlayers: w.players,
      plays: w.plays,
      ticketsIssued: w.plays,
      timeInAppSec: Math.round(w.hours * 3600),
      playsDistributionPct: [d1, d2, d3],
      quizzesStarted: w.started,
      playersStarted: Math.round(w.players * ratio),
    };
    return { week, f, ...w };
  });

  // Seasons: distinct counts scaled by the season's share of its full plays.
  const seasonFigures = SEASONS.map((s): SurveyFigures | null => {
    const own = weeks.filter((w) => w.week.periodId === s.id);
    const plays = own.reduce((t, w) => t + w.plays, 0);
    if (plays === 0) return null;
    const q = (plays / s.weeks.reduce((t, w) => t + w[2], 0)) ** 0.7;
    const scaled = (n: number) => Math.round(n * q);
    const both = Math.round(s.full.both * Math.min(1, q * 1.4));
    return {
      uniqueVisitors: scaled(s.full.visitors),
      optIns: scaled(s.full.optIns),
      uniquePlayers: scaled(s.full.players),
      plays,
      ticketsIssued: plays,
      timeInAppSec: own.reduce((t, w) => t + (w.week.timeInAppSec ?? 0), 0),
      consents: consentsOf(scaled(s.full.emailOnly), both, scaled(s.full.smsOnly)),
      quizzesStarted: own.reduce((t, w) => t + w.started, 0),
      playersStarted: scaled(s.full.playersStarted),
    };
  });

  // Campaign: members of Season 2 who were already in Season 1 count once.
  const [s1, s2] = seasonFigures;
  const dedupe = (pick: (f: SurveyFigures) => number) => (s1 ? pick(s1) : 0) + (s2 ? Math.round(pick(s2) * (1 - OVERLAP)) : 0);
  const add = (pick: (f: SurveyFigures) => number | null) => (s1 ? (pick(s1) ?? 0) : 0) + (s2 ? (pick(s2) ?? 0) : 0);
  const totals: SurveyFigures = !s1
    ? ZERO
    : {
        uniqueVisitors: dedupe((f) => f.uniqueVisitors),
        optIns: dedupe((f) => f.optIns),
        uniquePlayers: dedupe((f) => f.uniquePlayers),
        plays: add((f) => f.plays),
        ticketsIssued: add((f) => f.ticketsIssued),
        timeInAppSec: add((f) => f.timeInAppSec),
        consents: consentsOf(
          dedupe((f) => f.consents.emailOnly),
          add((f) => f.consents.emailAndSms),
          dedupe((f) => f.consents.smsOnly),
        ),
        quizzesStarted: add((f) => f.quizzesStarted),
        playersStarted: dedupe((f) => f.playersStarted ?? 0),
      };

  const live = weeks.find((w) => w.week.status === 'live');
  const lastStarted = [...weeks].reverse().find((w) => w.week.status !== 'planned');
  // Between the seasons the game is closed: not live, like the pipeline says.
  const inSeason = SEASONS.some((s) => a >= s.from && a <= s.to);
  return {
    campaign: {
      name: 'Système U — Quiz — Bien manger',
      start: GO_LIVE,
      end: iso(LAST_DAY),
      timezone: 'Europe/Paris',
      live: inSeason && !ended,
      currentWeek: live?.week.week ?? lastStarted?.week.week ?? 0,
      totalWeeks: TOTAL_WEEKS,
      weeklyPlayLimit: 3,
      periods: blankPeriods().map((p, i) => ({ ...p, totals: seasonFigures[i] })),
    },
    totals,
    weeks: weeks.map((w) => w.week),
    days: sampleDays(
      weeks.map((w) => w.week),
      a,
    ),
  };
}

/*
 * Per day — not drawn day by day in the design, so spread: each week's plays
 * over its days that have passed, with a weekday pattern (the week's last
 * measured day takes the rounding remainder, so the days add up to the week);
 * visitors run ~1.4× the surveys submitted.
 */
function sampleDays(weeks: SurveyWeek[], a: number): SurveyDay[] {
  const weight = (date: string) => WEEKDAY_WEIGHT[new Date(`${date}T12:00:00Z`).getUTCDay()];
  return Array.from({ length: LAST_DAY + 1 }, (_v, i) => iso(i)).map((date, i) => {
    const week = weeks.find((w) => date >= w.start && date <= w.end);
    if (i > a) return { date, uniqueVisitors: null, plays: null };
    // A day between the seasons: measured, nothing to play.
    if (!week) return { date, uniqueVisitors: 0, plays: 0 };
    if (!week.plays) return { date, uniqueVisitors: null, plays: null };
    const measured = Array.from({ length: dayIndex(week.end) - dayIndex(week.start) + 1 }, (_v, k) => iso(dayIndex(week.start) + k)).filter(
      (d) => dayIndex(d) <= a,
    );
    const total = measured.reduce((t, d) => t + weight(d), 0);
    const plays = week.plays;
    const share =
      date === measured[measured.length - 1]
        ? plays - measured.slice(0, -1).reduce((t, d) => t + Math.round((plays * weight(d)) / total), 0)
        : Math.round((plays * weight(date)) / total);
    return { date, uniqueVisitors: Math.round(share * VISITORS_PER_PLAY), plays: share };
  });
}

/** The design's default snapshot: Season 1 over, Season 2 in its last week. */
export const PREVIEW_AS_OF = '2026-12-09';
export const SU_PREVIEW: SurveyPayload = previewPayload(PREVIEW_AS_OF);

/** Same structure, nothing measured — the deployed no-live-data state. */
export const SU_EMPTY: SurveyPayload = {
  campaign: { ...SU_PREVIEW.campaign, live: false, currentWeek: 0, periods: blankPeriods() },
  totals: ZERO,
  weeks: WEEK_SPANS.map(blankWeek),
  days: [],
};
